<template>
  <div>
    <div class="row">
      <div class="col-md-6">
        <h1>Booking #{{ booking.booking_ref || booking.old_booking_id }}</h1>
      </div>
      <div class="col-md-6" style="text-align: right;" v-if="userRole == 1">
        <button class="button mt-1" v-on:click="handleBookingClick">
          <span> Notify Customer of Change </span>
        </button>
      </div>
    </div>

    <div class="box mt-2">
      <dl class="summary">
        <dt class="summary__label">Customer</dt>
        <dd class="summary__value" v-if="booking.customer == null">
          None -
          <a
            href="#"
            v-on:click.stop.prevent="linkingCustomer = true"
            style="margin-left:0.5rem"
            v-if="userRole == 1"
            >Link a customer</a
          >
        </dd>
        <dd class="summary__value" v-else>
          <router-link
            v-if="booking.customer !== null"
            :to="{ name: 'customer-edit', params: { id: booking.customer_id } }"
            >
            {{ booking.customer.first_name }} {{ booking.customer.last_name }} 
            <template v-if="booking.customer.is_blacklisted">
              <svg class="svg-icon" style="width: 20px; height: 20px;vertical-align: middle;fill: currentColor;overflow: hidden;" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg"><path d="M211.968 272.298667a384 384 0 0 0 539.733333 539.733333L211.968 272.298667z m60.330667-60.330667l539.733333 539.733333a384 384 0 0 0-539.733333-539.733333zM512 981.333333C252.8 981.333333 42.666667 771.2 42.666667 512S252.8 42.666667 512 42.666667s469.333333 210.133333 469.333333 469.333333-210.133333 469.333333-469.333333 469.333333z" fill="#595959" /><path d="M512 512a128 128 0 1 1 0-256 128 128 0 0 1 0 256z m213.333333 213.333333H298.666667a213.333333 213.333333 0 0 1 426.666666 0z" fill="#595959" /></svg>
            </template>
            </router-link
          >
          <button
            v-on:click="unlinkCustomer"
            class="icon icon--unlink"
            v-if="userRole == 1"
          ></button>
          <button class="icon icon--notes" style="margin-left: 0.5rem;" 
                  title="Customer Notes" @click="showCustomerNotesModal"
            ></button>
        </dd>

        <dt class="summary__label">Agent</dt>
        <dd class="summary__value" v-if="booking.agent == null">
          None -
          <a
            href="#"
            v-on:click.stop.prevent="linkingAgent = true"
            style="margin-left:0.5rem" v-if="userRole == 1"
            >Link an agent</a
          >
        </dd>
        <dd class="summary__value" v-else>
          <router-link
            :to="{ name: 'agent-edit', params: { id: booking.agent_id } }"
            >{{ booking.agent.name }} ({{ booking.agent.abta }})</router-link
          >
          <button v-on:click="unlinkAgent" class="icon icon--unlink" v-if="userRole == 1"></button>
        </dd>

        <dt class="summary__label">Affiliate</dt>
        <dd class="summary__value" v-if="booking.affiliate == null">
          None -
          <a
            href="#"
            v-on:click.stop.prevent="linkingAffiliate = true"
            style="margin-left:0.5rem"
            v-if="userRole == 1"
            >Link an affiliate</a
          >
        </dd>
        <dd class="summary__value" v-else>
          <router-link
            :to="{
              name: 'affiliate-edit',
              params: { id: booking.affiliate.id }
            }"
            >{{ booking.affiliate.name }} ({{
              booking.affiliate.code
            }})</router-link
          >
          <button
            v-on:click="unlinkAffiliate"
            v-if="userRole == 1"
            class="icon icon--unlink"
          ></button>
        </dd>

        <template v-if="booking.agent">
          <dt class="summary__label">Confirmed by Agent</dt>
          <dd class="summary__value" v-if="booking.agent_confirmed">
            Yes
          </dd>
          <dd class="summary__value" v-else>
            No - <a href="#" v-on:click.stop.prevent="confirmFromAgent()" style="margin-left:0.5rem"> Mark Confirmed </a>
          </dd>

        </template>

        <template v-if="booking.status !== null">
          <dt class="summary__label">Status</dt>
          <dd class="summary__value">
            <strong v-if="booking.status == 'Cancelled'">{{
              booking.status
            }}</strong
            ><span v-else>{{ booking.status }}</span>
          </dd>
        </template>

        <template v-if="booking.created_at">
          <dt class="summary__label">Date booked</dt>
          <dd class="summary__value">
            {{ booking.created_at | formatDateTime }}
          </dd>
        </template>

        <template v-if="booking.updated_at">
          <dt class="summary__label">Last updated</dt>
          <dd class="summary__value">
            {{ booking.updated_at | formatDateTime }}
          </dd>
        </template>

        <template>
          <dt class="summary__label">Booking reference</dt>
          <dd class="summary__value">
            <input type="text" v-model="booking.booking_ref" :readonly="userRole != 1" />
          </dd>
        </template>

        <template>
          <dt class="summary__label">Created By</dt>
          <dd class="summary__value">
            {{ booking.created_by ? booking.created_by.name : "N/A" }}
          </dd>
        </template>
        <template>
          <dt class="summary__label">Bonding Type</dt>
          <dd class="summary__value">
            <select v-model="booking.bonding_type">
                <option v-for="bonding in bondingTypes" :value="bonding.id" :key="bonding.name">{{ bonding.name }}</option>
            </select>
          </dd>
        </template>

        <template>
          <dt class="summary__label">Created From</dt>
          <dd class="summary__value">
            {{ booking.created_from ? booking.created_from  : "N/A" }}
          </dd>
        </template>

        <template v-if="booking.created_by_agent_name">
          <dt class="summary__label">Agent Name</dt>
          <dd class="summary__value">
            {{ booking.created_by_agent_name }}
          </dd>
        </template>
        <template v-if="showChangeSummaryTags">
          <dt class="summary__label"> Changes Accepted </dt>
          <dd class="summary__value">
            <span class="badge cursor" :class="(booking.changes_accepted == 1 ) ? 'badge-success':'badge-danger'" :title="changeAcceptedTitle">
              {{ (booking.changes_accepted == 1 ) ? 'Accepted' : 'Pending' }}
            </span>
          </dd>
        </template>

        <template v-if="showChangeSummaryTags">
          <dt class="summary__label"> Changes Reviewed </dt>
          <dd class="summary__value">
            <span class="badge cursor" :class="(booking.changes_reviewed == 1 ) ? 'badge-success':'badge-danger'" :title="changeReviewedTitle">
              {{ (booking.changes_reviewed == 1 ) ? 'Reviewed':'Pending' }}
            </span>
          </dd>
        </template>
        
        <template v-if="booking.changes_accepted == 0">
          <dd class="summary__value">
            <div class="col-10 mb-4">
              <label for="custom_airline_ref_email" class="fw-bold"> Accept Changes ? </label><br>
              <label class="switch mt-1">
                <input id="custom_airline_ref_email" type="checkbox" v-model="booking_acceptance" v-on:change="handleBookingChangeAcceptance"/>
                <span class="switch__slider"></span>
              </label>
            </div>
          </dd>
        </template>

      </dl>
      <div class="row p-3">
        <div class="col-md-12" style="display: flex; justify-content: space-between;">
          <button class="button mt-1" @click="$emit('handleBooking')" v-if="(booking.customer_id !==null || booking.agent_id !==null )">
            <span>Update booking</span>
          </button>
          <template v-if="!booking.cancelled_at && booking.created_at">
            <a href="#" style="font-weight: bold;" @click.stop.prevent="cancelBooking" v-if="userRole == 1">Cancel booking</a>
          </template>
        </div>
      </div>
    </div>
    
    <div v-if="booking.cancelled_at" class="mb-3">
      <div class="row mt-4 px-2">
        <div class="col-md-12">
          <h1>Cancellation Details</h1>
        </div>
      </div>
      <div class="row px-3">
        <div class="box mt-2">
          <dl class="summary">
            <template>
              <dt class="summary__label">Cancelled at</dt>
              <dd class="summary__value">
                {{ booking.cancelled_at | formatDateTime }}
              </dd>
            </template>  

            <template v-if="booking.cancelled_at">
              <dt class="summary__label">Cancellation Charges</dt>
              <dd class="summary__value">
                <input type="text" v-model="booking.cancellation_charge" />
              </dd>
            </template>
          </dl>
        </div>
      </div>
    </div>
    <div class="box" v-if="linkingCustomer == true">
      <div class="box__header">
        <h3>
          Begin by selecting a customer below, or
          <button class="button" v-on:click="creatingCustomer = true">
            create a customer
          </button>
        </h3>

        <div class="modal" v-if="creatingCustomer == true">
          <CustomerCreate></CustomerCreate>
          <button
            type="button"
            class="icon icon--unlink"
            v-on:click="creatingCustomer = false"
          >
            Close
          </button>
        </div>
        <div class="modal__bg" v-if="creatingCustomer == true"></div>

        <form class="form form--inline" @submit.stop.prevent="doCustomerSearch">
          <input
            v-model="customerSearch"
            type="search"
            placeholder="Search the customer database by name, email, phone or postcode"
          />
          <button type="submit" class="button button--small">Search</button>
        </form>
      </div>
      <!-- / box__header -->

      <vdtnet-table
        ref="customersTable"
        :fields="customerFields"
        :opts="customerTableOptions"
        @select="selectCustomer"
      >
      </vdtnet-table>
    </div>

    <div class="box" v-if="linkingAgent == true">
      <div class="box__header">
        <h3>
          Select an agent below, or
          <a v-on:click="creatingAgent = true">create an agent</a>
        </h3>

        <div class="modal" v-if="creatingAgent == true">
          <AgentCreate></AgentCreate>
          <button
            type="button"
            class="icon icon--unlink"
            v-on:click="creatingAgent = false"
          >
            Close
          </button>
        </div>
        <div class="modal__bg" v-if="creatingAgent == true"></div>

        <form class="form form--inline" @submit.stop.prevent="doAgentSearch">
          <input
            v-model="agentSearch"
            type="search"
            placeholder="Search the agents database by name, email, phone or postcode"
          />
          <button type="submit" class="button button--small">Search</button>
        </form>
      </div>
      <!-- / box__header -->

      <vdtnet-table
        ref="agentsTable"
        :fields="agentFields"
        :opts="agentTableOptions"
        @select="selectAgent"
      >
      </vdtnet-table>
    </div>

    <div class="box" v-if="linkingAffiliate == true">
      <div class="box__header">
        <h3>
          Select an affiliate below, or
          <a v-on:click="creatingAffiliate = true">create an affiliate</a>
        </h3>

        <div class="modal" v-if="creatingAffiliate == true">
          <AffiliateCreate></AffiliateCreate>
          <button
            type="button"
            class="icon icon--unlink"
            v-on:click="creatingAffiliate = false"
          >
            Close
          </button>
        </div>
        <div class="modal__bg" v-if="creatingAffiliate == true"></div>

        <form
          class="form form--inline"
          @submit.stop.prevent="doAffiliateSearch"
        >
          <input
            v-model="affiliateSearch"
            type="search"
            placeholder="Search the affiliates database by name, email, phone or postcode"
          />
          <button type="submit" class="button button--small">Search</button>
        </form>
      </div>
      <!-- / box__header -->

      <vdtnet-table
        ref="affiliatesTable"
        :fields="affiliateFields"
        :opts="affiliateTableOptions"
        @select="selectAffiliate"
      >
      </vdtnet-table>
    </div>

    <!-- Customer notes Modal -->
    <div class="modal-lg" v-if="showCustomerNoteModal == true">
      <CustomerNotesModal :customerID="booking.customer_id" :customerNote="customerNotesList"/> 
      <button type="button" class="icon icon--unlink" v-on:click="showCustomerNoteModal = false">Close</button>
    </div>
    <div class="modal__bg" v-if="showCustomerNoteModal == true"></div>
    <!-- Customer notes Modal -->

    <!-- Change Reasons Modal -->
    <div class="modal-lg" v-if="showBookingChangeReasonModal == true">
      <ChangeReasonModal 
        buttonText="Update"
        :bookingChangeReasons="bookingChangeReasons" 
        v-model="selectedChangeReasons"
        @triggerEmail="triggerEmail"/>
      <button type="button" class="icon icon--unlink" v-on:click="showBookingChangeReasonModal = false">Close</button>
    </div>
    <div class="modal__bg" v-if="showBookingChangeReasonModal == true"></div>
    <!-- Customer notes Modal -->

  </div>
</template>

<script>
import VdtnetTable from "vue-datatables-net";
import CustomerCreate from "@/views/CustomerCreate.vue";
import AgentCreate from "@/views/AgentCreate.vue";
import AffiliateCreate from "@/views/AffiliateCreate.vue";
import CustomerNotesModal from "@/components/CustomerNotesModal.vue"
import ChangeReasonModal from "@/components/BookingEdit/ChangeReasonModal.vue"

export default {
  name: "BookingSummary",
  props: {
    booking: Object,
    bondingTypes: Object,
    bookingChangeReasons:Array
  },
  components: {
    CustomerCreate,
    AgentCreate,
    AffiliateCreate,
    VdtnetTable,
    CustomerNotesModal,
    ChangeReasonModal
  },
  computed: {
    userRole() {
      let user_role = localStorage.getItem("user_role");
      return user_role
    },
    changeAcceptedTitle() {
      let title = null
      let accepted_from = null
      switch (this.booking.changes_accepted_from) {
        case 'by_consent': accepted_from = ', By Admin'; break;
        case 'email': accepted_from = ', From email'; break;
        case 'manage_booking': accepted_from = ', From Manage booking'; break;
      
        default:
          break;
      }
      if (this.booking.changes_accepted == 1) {
        title = (this.booking.changes_accepted_at) ? `On ${this.booking.changes_accepted_at} ${accepted_from}` : 'Never Accepted'
      }else if (this.booking.changes_accepted_at){
        title = `Last Accepted at ${this.booking.changes_accepted_at}`
      }
      return title
    },
    changeReviewedTitle(){
      let title = null
      if (this.booking.changes_reviewed == 1) {
        title = (this.booking.changes_reviewed) ? `On ${this.booking.changes_reviewed_at}` : 'Never Reviewed'
      }else if(this.booking.changes_reviewed_at){
        title = `Last Reviewed at ${this.booking.changes_reviewed_at}`
      }
      return title
    },
    showChangeSummaryTags(){
      if (this.booking.changes_accepted_at  || this.booking.changes_accepted == 0) {
        return true;
      }else{
        return false;
      }
    }
  },
  data() {
    return {
      creatingAgent: false,
      creatingCustomer: false,
      creatingAffiliate: false,
      linkingCustomer: false,
      linkingAgent: false,
      linkingAffiliate: false,
      customerSearch: "",
      agentSearch: "",
      affiliateSearch: "",

      customerTableOptions: {
        ajax: {
          url: this.$api + "customers/table",
          headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("token")
          }
        },
        serverSide: true,
        dom: "rt",
        pageLength: 5,
        autoWidth: false,
        processing: true
      },
      customerFields: {
        last_name: { label: "Last Name" },
        first_name: {
          label: "First Name",
          render: (d,t,f,m) => {
            return `${d} ${(f.is_blacklisted) ? '(Blacklisted)' : ''}`
          }
        },
        email: {
          label: "Email",
          render: data => {
            return data !== null
              ? `<a target="_blank" href="mailto://${data}">${data}</a>`
              : "";
          }
        },
        phone_number: { label: "Telephone" },
        postcode: { label: "Postcode" },
        actions: {
          searchable: false,
          sortable: false,
          label: " ",
          render: () => {
            return `<button data-action="select" class="button button--small">Select</button>`;
          }
        }
      },
      agentTableOptions: {
        ajax: {
          url: this.$api + "agents/table",
          headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("token")
          }
        },
        serverSide: true,
        dom: "rt",
        pageLength: 5,
        autoWidth: false
      },
      agentFields: {
        abta: { label: 'ABTA no.'},
        name: { label: "Name" },
        actions: {
          searchable: false,
          sortable: false,
          label: " ",
          render: () => {
            return `<button data-action="select" class="button button--small">Select</button>`;
          }
        }
      },
      affiliateTableOptions: {
        ajax: {
          url: this.$api + "affiliates/table",
          headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("token")
          }
        },
        serverSide: true,
        dom: "rt",
        pageLength: 5,
        autoWidth: false
      },
      affiliateFields: {
        name: { label: "Name" },
        code: { label: "Code" },
        actions: {
          searchable: false,
          sortable: false,
          label: " ",
          render: () => {
            return `<button data-action="select" class="button button--small">Select</button>`;
          }
        }
      },
      booking_acceptance:false,
      customerNotesList:[],
      showCustomerNoteModal:false,
      showBookingChangeReasonModal: false,
      selectedChangeReasons: [],
    };
  },
  mounted() {
    if(this.$route.query.customer_id) {
      this.getCustomerById(this.$route.query.customer_id);
    }
  },
  methods: {
    selectCustomer(data) {
      this.linkingCustomer = false;
      this.booking.customer = data;
      this.booking.customer_id = data.id;
    },
    unlinkCustomer() {
      this.booking.customer = null;
      this.booking.customer_id = null;
    },
    doCustomerSearch() {
      this.$refs.customersTable.search(this.customerSearch);
    },
    createAgent() {
      this.$data.creatingAgent = true;
    },
    selectAgent(data) {
      this.linkingAgent = false;
      this.booking.agent = data;
      this.booking.agent_id = data.id;
    },
    unlinkAgent() {
      this.booking.agent = null;
      this.booking.agent_id = null;
    },
    doAgentSearch() {
      this.$refs.agentsTable.search(this.agentSearch);
    },
    createAffiliate() {
      this.$data.creatingAffiliate = true;
    },
    selectAffiliate(data) {
      this.linkingAffiliate = false;
      this.booking.affiliate = data;
      this.booking.affiliate_id = data.id;
    },
    unlinkAffiliate() {
      this.booking.affiliate = null;
      this.booking.affiliate_id = null;
    },
    doAffiliateSearch() {
      this.$refs.affiliatesTable.search(this.affiliateSearch);
    },
    cancelBooking() {
      const r = confirm('Click "OK" to confirm cancellation.');
      if (r == true) {
        this.$store.dispatch("setLoadStatus");
        fetch(this.$api + "bookings/" + this.$route.params.id + "/cancel", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: localStorage.getItem("token")
          }
        })
          .then(res => res.json())
          .then(response => {
            if (response.success) {
              this.$notify({
                type: "success",
                title: response.message
              });
              this.$parent.getBooking();
            } else {
              this.$notify({
                type: "error",
                title: response.message
              });
            }
            this.$store.dispatch("setLoadStatus");
          });
      }
    },
    confirmFromAgent() {
      this.$dialog
      .confirm(`Are you sure you want to confirm this booking from agent's side? Please note that this booking will be considered as confirmed even if there is no payment recorded fot this booking.`)
      .then(() => {
        this.$store.dispatch('setLoadStatus')
        fetch(this.$api + "bookings/" + this.$route.params.id + "/agent-confirm", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: 'application/json',
            Authorization: localStorage.getItem('token')
          }
        })
        .then(res => res.json())
        .then(response => {
          if (response.success) {
              this.$notify({
                type: "success",
                title: response.message
              });
              this.$parent.getBooking();
            } else {
              this.$notify({
                type: "error",
                title: response.message
              });
            }
            this.$store.dispatch("setLoadStatus");
        })
      })
    },
    getCustomerById(id) {
      this.$store.dispatch('setLoadStatus')
      fetch(this.$api + 'customers/' + id, {
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('token')
        }
      })
      .then(res => res.json())
      .then(response => {
        this.$store.dispatch('setLoadStatus')
        if(response.data && response.data.length > 0) {
          this.selectCustomer(response.data[0])
        }
      })
    },
    handleBookingClick() {
      this.showBookingChangeReasonModal = true
    },
    triggerEmail(selectedReasons) {
      this.$emit('handleBooking', true,selectedReasons)
      this.showBookingChangeReasonModal = false;
    },
    handleBookingChangeAcceptance() {
      this.$swal({
        title: "Accept Changes ?",
        text: "Do you wish to sent email to customer ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes!",
        cancelButtonText: "No!"
      }).then((result) => {
        let sendEmailToCustomer = 'no';
        if(result.isConfirmed) {
          sendEmailToCustomer = 'yes'
        } 
        this.$store.dispatch("setLoadStatus");
        fetch(this.$api + "bookings/" + this.$route.params.id + "/accept-booking-change", {
          method: "POST",
          body: JSON.stringify({
            send_email:sendEmailToCustomer
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: localStorage.getItem("token")
          }
        })
          .then(res => res.json())
          .then(response => {
            if (response.status) {
              this.$notify({
                type: "success",
                title: response.message
              });
              this.booking.changes_accepted = 1
              this.booking.changes_reviewed = 1
              this.booking_acceptance = false
              if (response.data.accepted_at) {
                this.booking.changes_accepted_at = response.data.accepted_at 
              }

            } else {
              this.$notify({
                type: "error",
                title: response.message
              });
            }
            this.$store.dispatch("setLoadStatus");
          });

      })
      // const r = confirm('Click "OK" to confirm Acceptance.');
      // if (r == true) {
      
      // }
    },
    showCustomerNotesModal() {
      console.log('This Runs');
      this.$store.dispatch('setLoadStatus')
      fetch(`${this.$api}customer-note/get-notes`, {
          method: 'POST',
          body: JSON.stringify({
              customer_id: this.booking.customer_id,
          }),
          headers: {
              "Content-Type": "application/json",
              'Accept': 'application/json',
              'Authorization': localStorage.getItem('token')
          }
      })
      .then(res => res.json())
      .then(response => {
          this.$store.dispatch('setLoadStatus')
          if (response.status) {
              this.customerNotesList = response.note_list
              this.showCustomerNoteModal = true
          }
          
      })
      .catch((error) => {
          this.$store.dispatch('setLoadStatus')
          
      })
      
    }
  }
};
</script>
<style>
span.cursor{
  cursor: pointer;
}
</style>